import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cpf, cnpj } from 'cpf-cnpj-validator';
import { updatePaymentPlans } from '@/actions/purchase';
import errorMonitoring from 'errorMonitoring/ErrorMonitoring';
import MercadoPagoTDCContext from './MercadoPagoTDCContext';
import mercadoPago from '../../../../payments/core/engines/mercadoPago';
import { formatCurrency } from '../../../utils/currency';

const propTypes = {
  children: PropTypes.node.isRequired,
  onSubmit: PropTypes.func.isRequired,
  initialFormMetadata: PropTypes.object.isRequired,
};

/**
 * Selects the per-purchase MercadoPago public key resolved by the backend
 * (income separation). Undefined when there is no per-company override.
 * @param {object} state - Redux state.
 * @returns {string|undefined} The MercadoPago public key for this purchase.
 */
const selectMercadoPagoPublicKey = (state) =>
  state.purchase?.getIn(['paymentCredentials', 'mercadoPago', 'publicKey']);

/**
 * Whether the backend answered with a paymentCredentials object at all. It always
 * includes mercadoPago.publicKey when MercadoPago is enabled, so the object being
 * present while the key is missing means the field was dropped or renamed — not that
 * this tenant is on a single account.
 * @param {object} state - Redux state.
 * @returns {boolean} True when the backend sent payment credentials.
 */
const selectHasPaymentCredentials = (state) =>
  Boolean(state.purchase?.getIn(['paymentCredentials']));

/**
 * MercadoPagoTDCProvider component.
 * Provide context for MercadoPago TDC form functionality.
 * @param {object} props - children, onSubmit, initialFormMetadata.
 * @param {React.ReactNode} props.children - Child components.
 * @param {Function} props.onSubmit - Form submission handler.
 * @param {object} props.initialFormMetadata - Initial form metadata configuration.
 * @returns {React.ReactElement} Provider component.
 */
const MercadoPagoTDCProvider = ({ children, onSubmit, initialFormMetadata }) => {
  const dispatch = useDispatch();
  const { features, env } = useSelector((state) => state.whitelabelConfig);
  // Income separation: the backend resolves the MercadoPago public key of the
  // account that will be charged for this purchase's company. Tokenizing the
  // card against that same account is required, otherwise MercadoPago rejects
  // the token. Absent (no per-company override) → fall back to the tenant key.
  const mercadoPagoPublicKey = useSelector(selectMercadoPagoPublicKey);
  const hasPaymentCredentials = useSelector(selectHasPaymentCredentials);
  const { t, i18n } = useTranslation('payment');
  const [tdcFormInstance, setTdcFormInstance] = useState(undefined);
  const [identificationTypes, setIdentificationTypes] = useState([]);
  const [cardBin, setCardBin] = useState('');
  const [formMetadata, setFormMetadata] = useState(initialFormMetadata);
  const formMetadataRef = useRef();
  const holderEmailInputRef = useRef();
  const holderNameInputRef = useRef();
  const identificationNumberInputRef = useRef();
  const identificationTypeInputRef = useRef();
  const tdcFormInstanceRef = useRef();
  const cardBinRef = useRef();
  const cardPaymentMethodReadyRef = useRef(false);
  const cardPaymentMethodRef = useRef();
  const cardPaymentMethodRequestIdRef = useRef(0);
  const receivedCardPaymentMethodRequestIdRef = useRef();
  formMetadataRef.current = formMetadata;
  tdcFormInstanceRef.current = tdcFormInstance;
  cardBinRef.current = cardBin;

  /**
   * Update form field values.
   * @param {object} fields - Field values to update.
   */
  const updateFields = (fields) => {
    const { holderName, holderEmail, identificationNumber } = fields;
    if (holderEmailInputRef.current && holderEmail) {
      const currentHolderEmailValue = holderEmailInputRef.current.value;
      if (!currentHolderEmailValue) {
        holderEmailInputRef.current.value = holderEmail;
      }
    }
    if (holderNameInputRef.current && holderName) {
      const currentHolderNameValue = holderNameInputRef.current.value;
      if (!currentHolderNameValue) {
        holderNameInputRef.current.value = holderName;
      }
    }
    if (identificationNumberInputRef.current && identificationNumber) {
      const currentIdentificationNumberValue = identificationNumberInputRef.current.value;
      if (!currentIdentificationNumberValue) {
        identificationNumberInputRef.current.value = identificationNumber;
      }
    }
  };

  /**
   * Update form metadata with validation status.
   * @param {Error|undefined} error - Validation error.
   * @param {string} fieldName - Name of the field being validated.
   * @param {boolean} ignoreIdentificationNumber - Whether to ignore identification number validation.
   */
  const updateFormMetadata = (error, fieldName, ignoreIdentificationNumber = true) => {
    if (fieldName === 'identificationNumber' && ignoreIdentificationNumber) return;
    const formMetadata = formMetadataRef.current;
    const updatedFormMetadata = {
      ...formMetadata,
      fields: {
        ...formMetadata.fields,
        [fieldName]: {
          ...formMetadata.fields[fieldName],
          hasError: Boolean(error),
          isValid: Boolean(!error),
        },
      },
    };
    formMetadataRef.current = updatedFormMetadata;
    setFormMetadata(updatedFormMetadata);
  };

  /**
   * Mark specified fields as invalid.
   * @param {Array<string>} invalidFieldsNames - Array of field names to mark as invalid.
   */
  const setFieldsAsInvalid = (invalidFieldsNames) => {
    const formMetadata = formMetadataRef.current;
    const updatedFields = invalidFieldsNames.reduce((updatedFields, fieldName) => {
      const newUpatedFields = {
        ...updatedFields,
        [fieldName]: {
          ...updatedFields[fieldName],
          hasError: true,
          isValid: false,
        },
      };
      return newUpatedFields;
    }, formMetadata.fields);
    const updatedFormMetadata = {
      ...formMetadata,
      fields: {
        ...formMetadata.fields,
        ...updatedFields,
      },
    };
    formMetadataRef.current = updatedFormMetadata;
    setFormMetadata(updatedFormMetadata);
  };

  /**
   * Unmount MercadoPago TDC form and reset metadata.
   */
  const unmountMercadoPagoTDCForm = () => {
    formMetadataRef.current = initialFormMetadata;
    cardPaymentMethodReadyRef.current = false;
    cardPaymentMethodRef.current = undefined;
    cardPaymentMethodRequestIdRef.current += 1;
    receivedCardPaymentMethodRequestIdRef.current = undefined;
    setFormMetadata(initialFormMetadata);
    tdcFormInstanceRef.current.unmount();
  };

  /**
   * Validate document number based on document type (CPF/CNPJ).
   * @returns {boolean} True if document is valid.
   */
  const validateDocumentNumber = () => {
    const { value } = identificationNumberInputRef.current;
    const { value: documentTypeValue } = identificationTypeInputRef.current;
    if (documentTypeValue === 'CPF' || documentTypeValue === 'CNPJ') {
      const valueWithOnlyDigits = value.replace(/\D/g, '');
      identificationNumberInputRef.current.value = valueWithOnlyDigits;
      if (documentTypeValue === 'CPF' && !cpf.isValid(valueWithOnlyDigits)) {
        updateFormMetadata(
          new Error('invalid identification number'),
          'identificationNumber',
          false,
        );
        return false;
      }
      if (documentTypeValue === 'CNPJ' && !cnpj.isValid(valueWithOnlyDigits)) {
        updateFormMetadata(
          new Error('invalid identification number'),
          'identificationNumber',
          false,
        );
        return false;
      }
    }
    updateFormMetadata(undefined, 'identificationNumber', false);
    return true;
  };

  /**
   * Mount MercadoPago TDC form with payment configuration.
   * @param {number} amount - Payment amount.
   */
  const mountMercadoPagoTDCForm = async (amount) => {
    // Bind the SDK instance to this purchase's account before building the card
    // form. initialize() is a no-op when the key is unchanged (see the engine),
    // so this only rebuilds the instance when a per-company key applies.
    const publicKey = mercadoPagoPublicKey || env?.mercadoPago?.publicKey;
    if (!mercadoPagoPublicKey && hasPaymentCredentials) {
      // The backend sent credentials but no MercadoPago key: the field was dropped or
      // renamed. Falling back to the tenant key would tokenize against the default
      // account and the charge would land there too — no error, nothing to see until
      // someone reconciles the books. Make it visible.
      errorMonitoring.notify({
        error: new Error('MercadoPago public key missing from purchase payment credentials'),
        info: {
          from: 'MercadoPagoTDCProvider:mountMercadoPagoTDCForm',
          usingTenantFallback: Boolean(publicKey),
        },
      });
    }
    if (!publicKey) {
      // Without a key there is nothing safe to do: getInstance() would return whatever
      // instance a previous purchase left behind — in a SPA that can be another
      // company's — and the card would be tokenized against that account. Bail out
      // instead; the form does not mount and the mistake is visible.
      errorMonitoring.notify({
        error: new Error('MercadoPago public key unavailable; card form not mounted'),
        info: { from: 'MercadoPagoTDCProvider:mountMercadoPagoTDCForm' },
      });
      return;
    }
    mercadoPago.initialize(publicKey);
    const mercadoPagoInstance = mercadoPago.getInstance();
    const cardForm = mercadoPagoInstance.cardForm({
      amount,
      iframe: true,
      form: {
        id: formMetadata.id,
        ...formMetadata.fields,
      },
      callbacks: {
        onFormMounted: (error) => {
          if (error) return console.warn('Form Mounted handling error: ', error);
        },
        onSubmit: async (event) => {
          event.preventDefault();
          const formMetadata = formMetadataRef.current;
          const hasInvalidIdentificationNumber = formMetadata.fields.identificationNumber.hasError;
          if (hasInvalidIdentificationNumber) return;
          const identificationNumberIsValid = validateDocumentNumber();
          if (!identificationNumberIsValid) return;
          const mpDeviceSessionId = window.MP_DEVICE_SESSION_ID;
          // Reuse the payment method already resolved by onPaymentMethodsReceived (covered by the
          // stale-request-id guard). Fall back to a fresh lookup only for a native form submit that
          // bypassed submitForm's readiness gate.
          const paymentMethod =
            cardPaymentMethodRef.current ||
            (await mercadoPago.getCardPaymentMethod({ bin: cardBinRef.current }));
          onSubmit({
            ...cardForm.getCardFormData(),
            mpDeviceSessionId,
            paymentMethodId: paymentMethod?.id || '',
            issuerId: paymentMethod?.issuer?.id || '',
          });
        },
        onIdentificationTypesReceived: (error, data) => {
          setIdentificationTypes(data);
        },
        // Stale-response guard: relies on the MercadoPago SDK invoking the function returned here
        // BEFORE the matching onPaymentMethodsReceived for the same fetch. That ordering holds in
        // MercadoPago.js v2 (a single finally block runs the onFetching callback, then the received
        // dispatcher) but is undocumented and version-dependent — revisit on an SDK bump.
        onFetching: (resource) => {
          if (resource !== 'paymentMethods') return undefined;
          const requestId = cardPaymentMethodRequestIdRef.current;
          return () => {
            receivedCardPaymentMethodRequestIdRef.current = requestId;
          };
        },
        onPaymentMethodsReceived: (error, data) => {
          if (
            receivedCardPaymentMethodRequestIdRef.current !== cardPaymentMethodRequestIdRef.current
          ) {
            return;
          }
          const paymentMethod = !error ? data?.[0] : undefined;
          cardPaymentMethodRef.current = paymentMethod;
          cardPaymentMethodReadyRef.current = Boolean(paymentMethod);
        },
        onInstallmentsReceived: (error, data) => {
          if (error) {
            console.warn('Error receiving installments:', error);
            return;
          }

          // Only sync MercadoPago installment data and modify UI if feature flag is enabled
          if (!features.SHOW_INSTALLMENTS_INTEREST) {
            return;
          }

          // Sync MercadoPago installment data to Redux paymentPlans and modify UI if feature flag is enabled
          // This allows choosing between payment gateway data vs platform's own calculations
          // This ensures the pricing breakdown shows the same values as the dropdown
          if (features.USE_PAYMENT_GATEWAY_INSTALLMENT_DATA && data && data.payer_costs) {
            const payerCosts = data.payer_costs || [];
            const newPaymentPlans = {
              visaMc: {},
              amex: {},
            };

            payerCosts.forEach((payerCost) => {
              const months = payerCost.installments || 1;
              const installmentRate = payerCost.installment_rate || 0;
              const monthlyPercentage = months > 0 ? installmentRate / months : 0;
              const monthlyPayment = payerCost.installment_amount || 0;
              const total = payerCost.total_amount || 0;

              // Only add valid plans (with positive monthly payment and total)
              if (monthlyPayment > 0 && total > 0) {
                newPaymentPlans.visaMc[months] = {
                  monthlyPayment,
                  total,
                  interest: installmentRate,
                  interestByMonth: monthlyPercentage,
                  fee: total - monthlyPayment * months,
                };
                // Copy to amex as well since MercadoPago Brazil typically uses same rates
                newPaymentPlans.amex[months] = { ...newPaymentPlans.visaMc[months] };
              }
            });

            // Only dispatch if we have at least one valid plan
            const hasValidPlans =
              Object.keys(newPaymentPlans.visaMc).length > 0 ||
              Object.keys(newPaymentPlans.amex).length > 0;

            if (hasValidPlans) {
              dispatch(updatePaymentPlans(newPaymentPlans));
            }

            // Modify installments select options to include installment_rate
            // Note: Using direct DOM manipulation because MercadoPago SDK controls the select element
            // and doesn't provide a React-friendly way to customize option text
            const installmentsSelect = document.getElementById(formMetadata.fields.installments.id);
            if (installmentsSelect) {
              // Update each option text to include interest rate
              // Match options to payerCosts by installment number (option.value) to handle placeholder options
              Array.from(installmentsSelect.options).forEach((option, index) => {
                // Skip placeholder options (empty value or no value attribute)
                if (!option.value || option.value === '') {
                  return;
                }

                // Parse installment number from option value
                const installmentsFromValue = parseInt(option.value, 10);

                // Find matching payerCost by installments number
                let payerCost = payerCosts.find((pc) => pc.installments === installmentsFromValue);

                // Fall back to index-offset if value-based matching fails
                // Count how many non-placeholder options we've seen before this one
                if (!payerCost) {
                  const optionsBeforeThis = Array.from(installmentsSelect.options)
                    .slice(0, index)
                    .filter((opt) => opt.value && opt.value !== '');
                  const offsetIndex = optionsBeforeThis.length;
                  if (offsetIndex < payerCosts.length) {
                    payerCost = payerCosts[offsetIndex];
                  }
                }

                // If we still don't have a matching payerCost, skip this option
                if (!payerCost) {
                  return;
                }

                const installmentRate = payerCost.installment_rate || 0;
                const installments = payerCost.installments || 1;
                const installmentAmount = payerCost.installment_amount || 0;
                const totalAmount = payerCost.total_amount || 0;

                // Format amount with localized currency
                const formattedAmount = formatCurrency(
                  installmentAmount,
                  env?.tripPriceDecimals || 2,
                  env.currency,
                  i18n.language,
                );

                const formattedTotalAmount = formatCurrency(
                  totalAmount,
                  env?.tripPriceDecimals || 2,
                  env.currency,
                  i18n.language,
                );
                const totalText = t('total_of', { total: formattedTotalAmount });

                // Choose singular or plural based on number of installments
                const paymentText = installments === 1 ? t('payment_of') : t('payments_of');

                // Create custom text based on interest rate
                if (installmentRate > 0) {
                  // installment_rate is the total rate percentage - divide by installments to get monthly rate
                  const monthlyPercentage = (installmentRate / installments).toFixed(2);
                  const interestText = t('interest_of', { interestByMonth: monthlyPercentage });
                  option.text = `${installments} ${paymentText} ${formattedAmount} (${totalText} - ${interestText})`;
                } else {
                  const noInterestText = t('without_interest');
                  option.text = `${installments} ${paymentText} ${formattedAmount} (${totalText} - ${noInterestText})`;
                }
              });
            }
          }
        },
        onValidityChange: updateFormMetadata,
        onError: (errors) => {
          if (!errors.length) return;
          const fieldsNames = Object.keys(initialFormMetadata.fields);
          fieldsNames.push('expirationMonth', 'expirationYear');
          const fieldsWithErrors = errors.reduce((fieldsWithErrors, error) => {
            const errorMessage = error.message;
            const errorMessageArray = errorMessage.split(' ');
            let fieldName = errorMessageArray.find((messageToken) => {
              return fieldsNames.includes(messageToken);
            });
            if (fieldName === 'expirationMonth' || fieldName === 'expirationYear') {
              fieldName = 'expirationDate';
            }
            if (!fieldsWithErrors.includes(fieldName)) {
              fieldsWithErrors.push(fieldName);
            }
            return fieldsWithErrors;
          }, []);
          setFieldsAsInvalid(fieldsWithErrors);
        },
        onBinChange: (bin) => {
          if (bin !== cardBinRef.current) {
            cardBinRef.current = bin;
            cardPaymentMethodReadyRef.current = false;
            cardPaymentMethodRef.current = undefined;
            cardPaymentMethodRequestIdRef.current += 1;
          }
          setCardBin(bin);
        },
      },
    });
    setTdcFormInstance(cardForm);
  };

  /**
   * Submit the TDC form.
   * @returns {Promise<void>} Resolves after requesting a fresh card token.
   */
  const submitForm = async () => {
    const cardNumberIsValid = formMetadataRef.current.fields.cardNumber.isValid;
    if (!cardNumberIsValid || !cardPaymentMethodReadyRef.current) {
      setFieldsAsInvalid(['cardNumber']);
      return;
    }

    try {
      await tdcFormInstanceRef.current.createCardToken();
      tdcFormInstanceRef.current.submit();
    } catch (error) {
      // Do not rethrow: submitForm is also invoked fire-and-forget (Checkout.js), and submit() is
      // already skipped on throw so a consumed token is never sent. Surface to telemetry instead of
      // swallowing, since token-creation failures are the failure class this flow guards against.
      errorMonitoring.notify({ error, info: { from: 'MercadoPagoTDCProvider:submitForm' } });
    }
  };

  const contextValue = {
    submitForm,
    mountMercadoPagoTDCForm,
    identificationTypes,
    formMetadata,
    holderEmailInputRef,
    holderNameInputRef,
    identificationNumberInputRef,
    updateFields,
    unmountMercadoPagoTDCForm,
    updateFormMetadata,
    identificationTypeInputRef,
    validateDocumentNumber,
    cardBin,
  };

  return (
    <MercadoPagoTDCContext.Provider value={contextValue}>{children}</MercadoPagoTDCContext.Provider>
  );
};

MercadoPagoTDCProvider.propTypes = propTypes;

export default MercadoPagoTDCProvider;
